import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import { Switch } from '@headlessui/react';
import ReactTooltip from 'react-tooltip';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import MetaPixelSection from './MetaPixelSection/MetaPixelSection';
import CustomConversionSection from './CustomConverstionSection/CustomConverstionSection';
import { ReactComponent as TriangleIcon } from 'assets/icons/triangle.svg';
import Drawer from 'components/Drawer/Drawer';
import useClinic from 'hooks/useClinic';
import DurationSection from './DurationSection/DurationSection';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import CommonButton from 'components/CommonButton/CommonButton';
import { AxiosResponse } from 'axios';
import { BAD_REQUEST } from 'utils/constants/statusCode';
import BookingRedirectSection from './BookingRedirectSection/BookingRedirectSection';
import GoogleTagManager from './GoogleTagManagerSection/GoogleTagManagerSection';
import AutoConfirmAppointmentSection from './AutoConfirmAppointmentSection/AutoConfirmAppointmentSection';
import ToggleDisplayOnSection from '../WidgetSection/ToggleDisplayOnSection/ToggleDisplayOnSection';
import AddressRequiredSection from './AddressRequiredSection/AddressRequiredSection';
import ConfirmationPatientNotificationSection from './ConfirmationPatientNotifcationSection/ConfirmationPatientNotifcationSection';

interface AdvancedTrackingDrawerProps {
  isShowing: boolean;
  setIsShowing: React.Dispatch<React.SetStateAction<boolean>>;
}

const AdvancedTrackingDrawer: FC<AdvancedTrackingDrawerProps> = ({
  isShowing,
  setIsShowing,
}) => {
  const { data, mutate } = useClinic();
  const { control, watch, reset, handleSubmit, formState } = useForm({
    mode: 'all',
    defaultValues: {
      unitDuration: 10,
      slotInterval: 0,
      metaPixelCode: '',
      customScript: '',
      googleTagManagerId: '',
      bookingSuccessRedirectUrl: '',
      isAutoConfirmAppointment: false,
      isWidgetVisible: true,
      isHomeAddressEnabled: false,
      isConfirmAppointmentNotificationPatient: false,
      isFamilyBookingEnabled: false,
    },
  });
  const { isDirty, isValid } = formState;

  const [isDurationSectionOpened, setIsDurationSectionOpened] = useState(true);
  const [isTrackingSectionOpened, setIsTrackingSectionOpened] = useState(true);

  useEffect(() => {
    if (!data) return;

    reset({
      unitDuration: data.unitDuration,
      slotInterval: data.slotInterval,
      customScript: data.customScript ? data.customScript : '',
      metaPixelCode: data.metaPixelCode ? data.metaPixelCode : '',
      googleTagManagerId: data.googleTagManagerId
        ? data.googleTagManagerId
        : '',
      bookingSuccessRedirectUrl: data.bookingSuccessRedirectUrl ?? '',
      isAutoConfirmAppointment: data.isAutoConfirmAppointment || false,
      isWidgetVisible: data.isWidgetVisible,
      isHomeAddressEnabled: data.isHomeAddressEnabled,
      isConfirmAppointmentNotificationPatient:
        data.isConfirmAppointmentNotificationPatient || false,
      isFamilyBookingEnabled: data.isFamilyBookingEnabled || false,
    });
  }, [data, reset]);

  const onSave = async ({
    slotInterval,
    customScript,
    metaPixelCode,
    googleTagManagerId,
    bookingSuccessRedirectUrl,
    isAutoConfirmAppointment,
    isWidgetVisible,
    isHomeAddressEnabled,
    isConfirmAppointmentNotificationPatient,
    isFamilyBookingEnabled,
  }: {
    slotInterval: number;
    customScript: string;
    metaPixelCode: string;
    googleTagManagerId: string;
    bookingSuccessRedirectUrl: string;
    isAutoConfirmAppointment: boolean;
    isWidgetVisible: boolean;
    isHomeAddressEnabled: boolean;
    isConfirmAppointmentNotificationPatient: boolean;
    isFamilyBookingEnabled: boolean;
  }) => {
    const promises: Promise<AxiosResponse<any, any>>[] = [];

    if (formState.dirtyFields.slotInterval) {
      promises.push(
        axiosInstance.patch('/clinics/slot-interval', { slotInterval })
      );
    }
    if (formState.dirtyFields.metaPixelCode) {
      promises.push(
        axiosInstance.patch('/clinics/meta-pixel-code', {
          metaPixelCode: metaPixelCode.trim(),
        })
      );
    }
    if (formState.dirtyFields.customScript) {
      promises.push(
        axiosInstance.patch('/clinics/custom-script', {
          customScript: customScript.trim(),
        })
      );
    }
    if (formState.dirtyFields.googleTagManagerId) {
      promises.push(
        axiosInstance.patch('/clinics/google-tag-manager-id', {
          googleTagManagerId: googleTagManagerId.trim(),
        })
      );
    }
    if (formState.dirtyFields.bookingSuccessRedirectUrl) {
      promises.push(
        axiosInstance.patch('/clinics/booking-success-redirect-url', {
          bookingSuccessRedirectUrl: bookingSuccessRedirectUrl.trim(),
        })
      );
    }
    if (formState.dirtyFields.isAutoConfirmAppointment) {
      promises.push(
        axiosInstance.patch('/clinics/auto-confirm-appointment', {
          isAutoConfirmAppointmentEnable: isAutoConfirmAppointment,
        })
      );
    }
    if (formState.dirtyFields.isWidgetVisible) {
      promises.push(
        axiosInstance.patch(`/clinics/widget-visibility`, {
          isWidgetVisible,
        })
      );
    }

    if (formState.dirtyFields.isHomeAddressEnabled) {
      promises.push(
        axiosInstance.patch(`/clinics/home-address-setting`, {
          isHomeAddressEnabled,
        })
      );
    }

    if (formState.dirtyFields.isConfirmAppointmentNotificationPatient) {
      promises.push(
        axiosInstance.patch(`/clinics/confirm-appointment-notification`, {
          isConfirmAppointmentNotificationPatient,
        })
      );
    }

    if (formState.dirtyFields.isFamilyBookingEnabled) {
      promises.push(
        axiosInstance.patch(`/clinics/family-booking-setting`, {
          isFamilyBookingEnabled,
        })
      );
    }

    try {
      await Promise.all(promises);
      renderToast({
        type: 'success',
        message: 'Updated settings successfully',
      });
      reset({
        slotInterval,
        customScript,
        metaPixelCode,
        googleTagManagerId,
        bookingSuccessRedirectUrl,
        isAutoConfirmAppointment,
        isWidgetVisible,
        isHomeAddressEnabled,
        isConfirmAppointmentNotificationPatient,
        isFamilyBookingEnabled,
      });
      await mutate();
      setIsShowing(false);
    } catch (error: any) {
      renderToast({
        type: 'error',
        message:
          error.response?.status === BAD_REQUEST
            ? error.response?.data.message
            : 'Something went wrong. Please try again later',
      });
    }
  };

  const onReset = () => {
    reset({
      unitDuration: data.unitDuration,
      slotInterval: data.slotInterval,
      customScript: data.customScript ? data.customScript : '',
      metaPixelCode: data.metaPixelCode ? data.metaPixelCode : '',
      googleTagManagerId: data.googleTagManagerId
        ? data.googleTagManagerId
        : '',
      bookingSuccessRedirectUrl: data.bookingSuccessRedirectUrl ?? '',
      isAutoConfirmAppointment: data.isAutoConfirmAppointment || false,
      isWidgetVisible: data.isWidgetVisible,
      isHomeAddressEnabled: data.isHomeAddressEnabled || false,
      isConfirmAppointmentNotificationPatient:
        data.isConfirmAppointmentNotificationPatient || false,
      isFamilyBookingEnabled: data.isFamilyBookingEnabled || false,
    });
  };

  return (
    <Drawer
      title="Advanced"
      isShowing={isShowing}
      setIsShowing={setIsShowing}
      onReset={onReset}
      footer={
        <div className="w-full h-6 flex px-2.4 justify-end items-center shadow-primary sticky bottom-0 bg-white">
          <CommonButton
            className="!min-w-[12rem]"
            onClick={handleSubmit(onSave)}
            disabled={!isDirty || !isValid}
            isLoading={formState.isSubmitting}
          >
            Save
          </CommonButton>
        </div>
      }
    >
      <div className="pb-2">
        <div className="flex flex-col gap-y-1.6 mb-4">
          <ToggleDisplayOnSection control={control} data={data} />
        </div>

        <div className="flex flex-col mb-4">
          <AutoConfirmAppointmentSection control={control} />
        </div>

        <div className="flex flex-col mb-4">
          <AddressRequiredSection control={control} />
        </div>

        <div className="flex flex-col mb-4">
          <ConfirmationPatientNotificationSection control={control} />
        </div>

        <div className="flex flex-col mb-4">
          <div className="flex justify-between items-center text-14 leading-[2.1rem]">
            <div className="flex items-center gap-x-0.8 mb-1">
              <h3 className="font-bold">Family Booking</h3>
              <button data-for="isFamilyBookingSection" data-tip="">
                <InfoIcon className="w-1.6 h-1.6 all-child:fill-mid-grey" />
              </button>
              {createPortal(
                <ReactTooltip
                  id="isFamilyBookingSection"
                  effect="solid"
                  place="bottom"
                  className="!bg-white !rounded-[0.8rem] !w-[18.5rem] !h-[auto] z-[9999] flex justify-center items-center !opacity-100 shadow-elevation-07 after:hidden before:hidden !px-[10px] !py-[4px]"
                >
                  <span className="text-11 text-darkest-grey flex items-center font-normal">
                    This setting only supports when split scheduling is off
                  </span>
                </ReactTooltip>,
                document.body
              )}
            </div>
          </div>
          <Controller
            name={'isFamilyBookingEnabled'}
            control={control}
            render={({ field }) => {
              return (
                <div className="flex gap-x-1.6 items-center">
                  <Switch
                    onChange={field.onChange}
                    checked={field.value}
                    className={`${
                      field.value ? 'bg-magenta' : 'bg-[#78788029]'
                    } relative inline-flex h-3.1 w-5.1 items-center rounded-full`}
                  >
                    <span
                      className={`${
                        field.value
                          ? 'translate-x-[2.3rem]'
                          : 'translate-x-[2px]'
                      } inline-block h-2.5 w-2.5 transform rounded-full transition bg-white`}
                    />
                  </Switch>
                  <span className="uppercase">
                    {field.value ? 'On' : 'Off'}
                  </span>
                </div>
              );
            }}
          />
        </div>

        <button
          className="flex gap-x-0.8 items-center cursor-pointer mt-4"
          onClick={() => setIsDurationSectionOpened((value) => !value)}
        >
          <TriangleIcon
            className={`${
              isDurationSectionOpened ? '' : '-rotate-90'
            } transition-transform`}
          />
          <span className="uppercase font-bold text-14 leading-[2.1rem] text-dim-grey">
            Duration
          </span>
        </button>
        {isDurationSectionOpened && (
          <div className="flex flex-col gap-y-1.6 mt-1.6">
            {/* SLOT INTEVAL DEPENDS ON UNIT DURATION */}
            <DurationSection
              control={control}
              unitDuration={watch('unitDuration')}
            />
          </div>
        )}

        <button
          className="flex gap-x-0.8 items-center cursor-pointer mt-4"
          onClick={() => setIsTrackingSectionOpened((value) => !value)}
        >
          <TriangleIcon
            className={`${
              isTrackingSectionOpened ? '' : '-rotate-90'
            } transition-transform`}
          />
          <span className="uppercase font-bold text-14 leading-[2.1rem] text-dim-grey">
            Tracking
          </span>
        </button>
        {isTrackingSectionOpened && (
          <div className="mt-1.6 space-y-[3rem]">
            <MetaPixelSection control={control} />
            <GoogleTagManager control={control} />
            <CustomConversionSection control={control} />
            <BookingRedirectSection control={control} />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default AdvancedTrackingDrawer;
